export const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const GRID_START_HOUR = 8;
export const GRID_END_HOUR = 18;
export const SLOTS_PER_HOUR = 2;
export const SUBROWS_PER_SLOT = 3;
export const SUBROWS_PER_HOUR = SLOTS_PER_HOUR * SUBROWS_PER_SLOT;
export const SLOT_MINUTES = 60 / SLOTS_PER_HOUR;
export const HEADER_ROWS = 1;
export const EVENT_ROW_SPAN = SUBROWS_PER_HOUR;

export const HOURS = [
    "8AM",
    "9AM",
    "10AM",
    "11AM",
    "12PM",
    "1PM",
    "2PM",
    "3PM",
    "4PM",
    "5PM",
];

export const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

export const getGridDates = (
    year: number,
    month: number,
): { date: Date; isCurrentMonth: boolean }[] => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);

    const totalDays = startOffset + lastDay.getDate();
    const rows = Math.ceil(totalDays / 7);
    const cellCount = rows * 7;

    const dates: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < cellCount; i++) {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        dates.push({
            date: d,
            isCurrentMonth: d.getMonth() === month,
        });
    }
    return dates;
};

export const formatDateKey = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export const formatTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
};

export const getWeekDays = (date: Date): Date[] => {
    const result: Date[] = [];
    const day = date.getDay();
    const diff = date.getDate() - day;

    for (let i = 0; i < 7; i++) {
        const d = new Date(date);
        d.setDate(diff + i);
        result.push(d);
    }
    return result;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

export const parseTimeToRow = (timeStr: string | null | undefined): number | null => {
    if (!timeStr) return null;

    const [hours, minutes] = timeStr.split(":").map(Number);

    if (hours < GRID_START_HOUR || hours >= GRID_END_HOUR) return null;

    const hourOffset = (hours - GRID_START_HOUR) * SUBROWS_PER_HOUR;
    const slotOffset = Math.floor(minutes / SLOT_MINUTES) * SUBROWS_PER_SLOT;
    return hourOffset + slotOffset + HEADER_ROWS + 1;
};

export interface EventLayout {
    row: number;
    columnIndex: number;
    totalColumns: number;
}

export const computeOverlapLayout = <T>(
    events: { event: T; row: number }[],
): { event: T; layout: EventLayout }[] => {
    if (events.length === 0) return [];

    const sorted = events.slice().sort((a, b) => a.row - b.row);

    const clusters: { event: T; row: number }[][] = [];
    let currentCluster: { event: T; row: number }[] = [sorted[0]];
    let clusterEnd = sorted[0].row + EVENT_ROW_SPAN;

    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].row < clusterEnd) {
            currentCluster.push(sorted[i]);
            clusterEnd = Math.max(clusterEnd, sorted[i].row + EVENT_ROW_SPAN);
        } else {
            clusters.push(currentCluster);
            currentCluster = [sorted[i]];
            clusterEnd = sorted[i].row + EVENT_ROW_SPAN;
        }
    }
    clusters.push(currentCluster);

    const results: { event: T; layout: EventLayout }[] = [];
    for (const cluster of clusters) {
        const totalColumns = cluster.length;
        cluster.forEach((item, columnIndex) => {
            results.push({
                event: item.event,
                layout: {
                    row: item.row,
                    columnIndex,
                    totalColumns,
                },
            });
        });
    }
    return results;
};

export const formatSidebarDate = (dateValue: string): string => {
    const [year, month, day] = dateValue.split("-").map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};
